import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse, ItemEntity, ItemListResponse, ItemLookupOption, ItemUpsertPayload } from '@/api/types'

function unwrap<T>(payload: any): T {
  if (payload && typeof payload === 'object') {
    if ('success' in payload && 'data' in payload) return payload.data as T
    if ('data' in payload) return payload.data as T
  }
  return payload as T
}

function firstNumber(v: any, fallback = 0): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

function firstString(v: any): string {
  return typeof v === 'string' ? v : ''
}

function asBool(v: any): boolean {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v === 1
  if (typeof v === 'string') return v.toLowerCase() === 'true' || v === '1'
  return false
}

function mapLookup(item: any): ItemLookupOption {
  return {
    id: firstNumber(
      item?.id ??
        item?.lookupId ??
        item?.value ??
        item?.itemCategoryId ??
        item?.itemcategoryId ??
        item?.subCategoryId
    ),
    nameEn: String(
      item?.lookupNameEn ??
        item?.nameEn ??
        item?.name ??
        item?.label ??
        item?.itemCategoryNameEn ??
        item?.itemcategoryNameEn ??
        item?.subCategoryNameEn ??
        ''
    ),
    nameAr: String(
      item?.lookupNameAr ??
        item?.nameAr ??
        item?.name ??
        item?.label ??
        item?.itemCategoryNameAr ??
        item?.itemcategoryNameAr ??
        item?.subCategoryNameAr ??
        ''
    ),
  }
}

function mapTaxLookup(item: any): ItemLookupOption {
  // Sales taxes lookup comes from /api/ApiItem/GetSalesTaxes
  return {
    id: firstNumber(item?.taxRateId ?? item?.TaxRateId ?? item?.id ?? item?.Id ?? item?.value ?? item?.lookupId),
    nameEn: String(
      item?.taxRateNameEn ??
        item?.TaxRateNameEn ??
        item?.lookupNameEn ??
        item?.nameEn ??
        item?.NameEn ??
        item?.name ??
        item?.Name ??
        item?.label ??
        ''
    ),
    nameAr: String(
      item?.taxRateNameAr ??
        item?.TaxRateNameAr ??
        item?.lookupNameAr ??
        item?.nameAr ??
        item?.NameAr ??
        item?.name ??
        item?.Name ??
        item?.label ??
        ''
    ),
  }
}

function mapItem(item: any): ItemEntity {
  return {
    itemId: firstNumber(item?.itemId ?? item?.id),
    itemNameEn: firstString(item?.itemNameEn),
    itemNameAr: firstString(item?.itemNameAr),
    itemCode: firstString(item?.itemCode),
    reference: firstString(item?.reference),
    itemCategoryId: firstNumber(item?.itemCategoryId),
    sku: firstString(item?.sku),
    barcode: firstString(item?.barcode),
    tags: firstString(item?.tags),
    shelfId: firstString(item?.shelfId),
    description: firstString(item?.description),
    subCategoryId: firstNumber(item?.subCategoryId),
    uom: firstNumber(item?.uom),
    type: firstNumber(item?.type),
    costMethod: firstNumber(item?.costMethod),
    isTaxable: asBool(item?.isTaxable),
    tax: firstNumber(item?.tax),
    sellingTax: firstNumber(item?.sellingTax),
    attachmentUrl: firstString(item?.attachmentFile ?? item?.attachmentUrl ?? item?.imageUrl) || undefined,
    createdDate: firstString(item?.createdDate) || undefined,
    createdBy: item?.createdBy !== undefined ? firstNumber(item?.createdBy) : undefined,
    modifiedDate: firstString(item?.modifiedDate) || undefined,
    modifiedBy: item?.modifiedBy !== undefined ? firstNumber(item?.modifiedBy) : undefined,
    branchId: firstNumber(item?.branchId),
  }
}

function toArray(payload: any): any[] {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.result)) return payload.result
  return []
}

function toTotalCount(payload: any, fallback: number): number {
  const candidates = [
    payload?.totalCount,
    payload?.totalRecords,
    payload?.recordCount,
    payload?.count,
    payload?.pagination?.total,
  ]
  for (const c of candidates) {
    const n = Number(c)
    if (Number.isFinite(n)) return n
  }
  return fallback
}

function formDataFromPayload(payload: ItemUpsertPayload): FormData {
  const fd = new FormData()
  fd.append('ItemId', String(payload.itemId || 0))
  fd.append('ItemNameEn', payload.itemNameEn || '')
  fd.append('ItemNameAr', payload.itemNameAr || '')
  fd.append('ItemCode', payload.itemCode || '')
  fd.append('Reference', payload.reference || '')
  fd.append('ItemCategoryId', String(payload.itemCategoryId || 0))
  fd.append('Sku', payload.sku || '')
  fd.append('Barcode', payload.barcode || '')
  fd.append('Tags', payload.tags || '')
  fd.append('ShelfId', payload.shelfId || '')
  fd.append('Description', payload.description || '')
  fd.append('SubCategoryId', String(payload.subCategoryId || 0))
  fd.append('Uom', String(payload.uom || 0))
  fd.append('Type', String(payload.type || 0))
  fd.append('CostMethod', String(payload.costMethod || 0))
  fd.append('IsTaxable', String(Boolean(payload.isTaxable)))
  fd.append('Tax', String(payload.tax || 0))
  fd.append('SellingTax', String(payload.sellingTax || 0))
  fd.append('CreatedDate', payload.createdDate || new Date().toISOString())
  fd.append('CreatedBy', String(payload.createdBy || 0))
  fd.append('ModifiedDate', payload.modifiedDate || new Date().toISOString())
  fd.append('ModifiedBy', String(payload.modifiedBy || 0))
  fd.append('BranchId', String(payload.branchId || 0))
  if (payload.attachmentFile) {
    fd.append('AttachmentFile', payload.attachmentFile)
  }
  return fd
}

export class ItemService {
  async getAllItems(branchId: number, pageNumber: number, pageSize: number): Promise<ItemListResponse> {
    const response = await apiClient.get<ApiResponse<any> | any>(
      endpoints.items.getAll(branchId, pageNumber, pageSize)
    )
    const payload = unwrap<any>(response.data)
    const arr = toArray(payload).map(mapItem)
    const totalCount = toTotalCount(payload, arr.length)
    return { items: arr, totalCount }
  }

  async getItemById(id: number, branchId: number): Promise<ItemEntity> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.items.getById(id, branchId))
    const payload = unwrap<any>(response.data)
    return mapItem(payload)
  }

  async getCategories(branchId: number): Promise<ItemLookupOption[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.items.getAllItemCategory(branchId))
    const payload = unwrap<any>(response.data)
    return toArray(payload).map(mapLookup).filter((x) => x.id > 0)
  }

  async getSubCategories(branchId: number): Promise<ItemLookupOption[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.items.getAllItemSubCategory(branchId))
    const payload = unwrap<any>(response.data)
    return toArray(payload).map(mapLookup).filter((x) => x.id > 0)
  }

  async getMaterialTypesLookups(): Promise<ItemLookupOption[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.items.getMaterialTypesLookups)
    const payload = unwrap<any>(response.data)
    return toArray(payload).map(mapLookup).filter((x) => x.id > 0)
  }

  async getMaterialUOMsLookups(): Promise<ItemLookupOption[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.items.getMaterialUOMsLookups)
    const payload = unwrap<any>(response.data)
    return toArray(payload).map(mapLookup).filter((x) => x.id > 0)
  }

  async getSalesTaxes(branchId: number): Promise<ItemLookupOption[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.items.getSalesTaxes(branchId))
    const payload = unwrap<any>(response.data)
    return toArray(payload).map(mapTaxLookup).filter((x) => x.id > 0)
  }

  async getPurchaseTaxes(branchId: number): Promise<ItemLookupOption[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.items.getPurchaseTaxes(branchId))
    const payload = unwrap<any>(response.data)
    return toArray(payload).map(mapTaxLookup).filter((x) => x.id > 0)
  }

  async save(payload: ItemUpsertPayload): Promise<void> {
    await apiClient.post(endpoints.items.save, formDataFromPayload(payload))
  }

  async update(payload: ItemUpsertPayload): Promise<void> {
    await apiClient.post(endpoints.items.update, formDataFromPayload(payload))
  }
}

export const itemService = new ItemService()
